import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Trash2, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { ThemeToggle } from '@/components/ThemeToggle';
import { LegalFooter } from '@/components/LegalFooter';

export default function Settings() {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [username, setUsername] = useState('');
  const [currentUsername, setCurrentUsername] = useState<string | null>(null);
  const [savingUsername, setSavingUsername] = useState(false);
  const [usernameLoaded, setUsernameLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setUsernameLoaded(true); return; }
      const { data } = await supabase
        .from('profiles')
        .select('username')
        .eq('user_id', user.id)
        .maybeSingle();
      if (data?.username) {
        setCurrentUsername(data.username);
        setUsername(data.username);
      }
      setUsernameLoaded(true);
    })();
  }, []);

  const handleSaveUsername = async () => {
    const cleaned = username.trim().toLowerCase();
    if (cleaned === (currentUsername || '')) return;
    setSavingUsername(true);
    const { data, error } = await supabase.rpc('set_username', { p_username: cleaned });
    setSavingUsername(false);
    if (error) {
      toast({
        title: 'Could not save username',
        description: error.message,
        variant: 'destructive',
      });
      return;
    }
    setCurrentUsername(data as string);
    setUsername(data as string);
    toast({ title: 'Username saved', description: `You are now @${data}` });
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirm !== 'delete') return;
    setDeleting(true);
    const { error } = await supabase.functions.invoke('delete-account', { method: 'POST' });
    if (error) {
      setDeleting(false);
      toast({
        title: 'Could not delete account',
        description: error.message,
        variant: 'destructive',
      });
      return;
    }
    toast({ title: 'Account deleted', description: 'Your personal data has been removed.' });
    await signOut();
    navigate('/auth');
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-card/60 backdrop-blur-xl border-b border-border/40 sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-3 max-w-6xl flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            Dashboard
          </Button>
          <span className="text-lg font-display font-bold tracking-tight">Settings</span>
          <ThemeToggle />
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6 max-w-6xl">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Username</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Your public name for comments and your scout profile. 3–20 characters,
              lowercase letters, numbers and underscore only. This is shown publicly
              instead of your real name.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">@</span>
                <Input
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="pl-7"
                  placeholder="your_username"
                  maxLength={20}
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  disabled={savingUsername || !usernameLoaded}
                />
              </div>
              <Button
                onClick={handleSaveUsername}
                disabled={savingUsername || !usernameLoaded || username.trim().toLowerCase() === (currentUsername || '')}
              >
                {savingUsername ? 'Saving…' : currentUsername ? 'Update' : 'Set username'}
              </Button>
            </div>
            {currentUsername && (
              <span className="text-xs text-muted-foreground">
                Current: @{currentUsername}
              </span>
            )}
          </CardContent>
        </Card>

        <Card className="border-destructive/40 bg-destructive/5">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-4 w-4" />
              Danger zone
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <p className="text-sm font-medium">Delete your account</p>
              <p className="text-xs text-muted-foreground mt-1">
                Permanently removes your profile, watchlist, comments, badges and notifications.
                Your scouting ratings are kept in anonymized form so player aggregates stay accurate.
                This cannot be undone.
              </p>
            </div>
            <Button
              variant="destructive"
              onClick={() => { setDeleteConfirm(''); setDeleteOpen(true); }}
              className="gap-2 shrink-0"
            >
              <Trash2 className="h-4 w-4" />
              Delete account
            </Button>
          </CardContent>
        </Card>

        <LegalFooter />
      </main>

      <Dialog open={deleteOpen} onOpenChange={(o) => { if (!deleting) setDeleteOpen(o); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-4 w-4" />
              Delete your account?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p>
              This will <strong>permanently delete</strong> your profile, watchlist, comments,
              badges, notifications, and subscription record. Your scouting ratings will be
              anonymized and kept for aggregate player statistics.
            </p>
            <p className="text-muted-foreground">
              Type <span className="font-mono font-semibold text-foreground">delete</span> to confirm.
            </p>
            <Input
              autoFocus
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              placeholder="delete"
              disabled={deleting}
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteOpen(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteConfirm !== 'delete' || deleting}
              onClick={handleDeleteAccount}
              className="gap-2"
            >
              <Trash2 className="h-4 w-4" />
              {deleting ? 'Deleting…' : 'Delete forever'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
